import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, Image as ImageIcon, Receipt, MapPin } from "lucide-react";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { KpiCard } from "@/components/kpi-card";
import { clientForPortalUser, portalDataForClient } from "@/mocks/saas-data";
import { fmtUsd } from "@/mocks/data";

export const Route = createFileRoute("/portal/")({
  ssr: false,
  head: () => ({ meta: [{ title: "Portal — Home" }] }),
  component: PortalHome,
});

function PortalHome() {
  const user = useRequireRole(["client_portal"]);
  if (!user) return null;

  const client = clientForPortalUser(user.id);
  if (!client) {
    return (
      <div className="mx-auto max-w-3xl p-10 text-center text-sm text-muted-foreground">
        Your portal account isn't linked to a client yet. Please contact your studio.
      </div>
    );
  }
  const { shoots, galleries, invoices, contracts } = portalDataForClient(client.name);
  const nextShoot = shoots[0];
  const outstanding = invoices.filter((i) => i.status === "sent" || i.status === "overdue");
  const totalDue = outstanding.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title={`Hello, ${client.name.split(" ")[0]}.`}
        description="Your shoots, galleries, contracts, and invoices."
      />

      <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Upcoming Shoots" value={String(shoots.length).padStart(2, "0")} hint={nextShoot ? `Next: ${nextShoot.date}` : "None scheduled"} />
        <KpiCard label="Galleries" value={String(galleries.length).padStart(2, "0")} hint="Ready to view" />
        <KpiCard label="Open Invoices" value={fmtUsd(totalDue)} hint={`${outstanding.length} unpaid`} />
        <KpiCard label="Contracts" value={String(contracts.length).padStart(2, "0")} hint="Active & signed" />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2 space-y-3">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Upcoming shoots</h2>
          {shoots.length === 0 && (
            <p className="rounded-xl bg-card p-6 text-center text-sm text-muted-foreground ring-1 ring-border">
              No upcoming shoots.
            </p>
          )}
          {shoots.map((s) => (
            <Link key={s.id} to="/portal/shoots" className="block rounded-xl bg-card p-4 ring-1 ring-border hover:ring-foreground/20">
              <div className="flex items-center gap-6">
                <div className="flex flex-none flex-col items-center border-r border-border pr-6">
                  <Calendar className="size-4 text-muted-foreground" />
                  <span className="mt-1 font-mono text-sm font-medium">{s.callTime}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-medium">{s.title}</h3>
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="size-3" /> {s.location} · {s.date}
                  </p>
                </div>
                <StatusBadge status={s.status} />
              </div>
            </Link>
          ))}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Latest galleries</h2>
            <div className="grid grid-cols-2 gap-3">
              {galleries.slice(0, 4).map((g) => (
                <Link key={g.id} to="/portal/galleries" className="space-y-2">
                  <div
                    className="aspect-[4/5] w-full rounded-xl ring-1 ring-inset ring-border"
                    style={{ background: `linear-gradient(135deg, oklch(0.6 0.08 ${g.coverHue}) 0%, oklch(0.3 0.04 ${g.coverHue}) 100%)` }}
                  />
                  <div className="leading-tight">
                    <p className="truncate text-[11px] font-medium">{g.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {g.imageCount} img
                    </p>
                  </div>
                </Link>
              ))}
              {galleries.length === 0 && (
                <p className="col-span-2 text-xs text-muted-foreground">No galleries yet.</p>
              )}
            </div>
          </div>

          {outstanding.length > 0 && (
            <Link to="/portal/invoices" className="flex items-center justify-between rounded-xl border border-dashed border-border-strong bg-card/50 p-4 text-sm hover:bg-card">
              <span>
                <Receipt className="mr-2 inline size-4 text-muted-foreground" />
                Pay {fmtUsd(totalDue)} now
              </span>
              <span className="font-mono text-[10px] uppercase text-muted-foreground">{outstanding.length} due</span>
            </Link>
          )}
        </section>
      </div>
    </div>
  );
}
